/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Grid, Pagination, Typography2 } from '@coursera/cds-core';
import { Box } from '@coursera/coursera-ui';

import { BulkActionBar, BulkActionBarCheckbox } from 'bundles/enterprise-admin-skills/components/BulkActionBar';
import { Labeled, Spacer, formatNumber } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-skills';

export const SkillSetsSelectionControls: React.FC<{
  selectedSkillSetsCount?: number;
  skillSetsCount?: number;
  areAllSelected: boolean;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
  actions?: React.ReactNode;
}> = ({ selectedSkillSetsCount, skillSetsCount, areAllSelected, handleSelectAll, handleDeselectAll, actions }) => {
  let selectAllLabel;

  if (areAllSelected) {
    selectAllLabel = (
      <Typography2 component="p" color="invertBody">
        <FormattedMessage
          message={_t('Deselect All ({number} {number, plural, =1 {SkillSet} other {SkillSets}})')}
          number={formatNumber(skillSetsCount)}
        />
      </Typography2>
    );
  } else if (!selectedSkillSetsCount) {
    selectAllLabel = (
      <Typography2 component="p" color="highlightBlue">
        <FormattedMessage
          message={_t('Select All ({number} {number, plural, =1 {SkillSet} other {SkillSets}})')}
          number={formatNumber(skillSetsCount)}
        />
      </Typography2>
    );
  } else {
    selectAllLabel = (
      <Typography2 component="p" color="invertBody">
        <FormattedMessage
          message={_t('Select All ({number} {number, plural, =1 {SkillSet} other {SkillSets}})')}
          number={formatNumber(skillSetsCount)}
        />
      </Typography2>
    );
  }

  return (
    <BulkActionBar isActive={Boolean(selectedSkillSetsCount)}>
      <Labeled>
        <Box alignItems="center">
          <BulkActionBarCheckbox
            checked={areAllSelected}
            onChange={areAllSelected ? handleDeselectAll : handleSelectAll}
          />
          <Spacer size={12} />
          {selectAllLabel}
        </Box>
      </Labeled>
      {selectedSkillSetsCount ? (
        <Typography2
          color="invertBody"
          component="strong"
          style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}
        >
          <FormattedMessage
            message={_t('{number} {number, plural, =1 {SkillSet} other {SkillSets}} selected')}
            number={formatNumber(selectedSkillSetsCount)}
          />
        </Typography2>
      ) : null}
      <Box>{actions}</Box>
    </BulkActionBar>
  );
};

export const SkillSetsPaginationControls = ({
  currentPage,
  onPageChange,
  limit,
  onLimitChange,
  skillSetsCount,
  hideSkillSetsCount,
}: {
  hideSkillSetsCount?: boolean;
  skillSetsCount?: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  limit: number;
}) => {
  return (
    <Grid
      container
      direction="row"
      justifyContent={!hideSkillSetsCount ? 'space-between' : 'flex-end'}
      alignItems="center"
    >
      {!hideSkillSetsCount && (
        <Grid xs={3}>
          <Typography2 component="strong">
            <FormattedMessage
              message={_t('{number} {number, plural, =1 {SkillSet} other {SkillSets}}')}
              number={formatNumber(skillSetsCount)}
            />
          </Typography2>
        </Grid>
      )}
      <Grid container direction="row" xs={9} justifyContent="flex-end" alignItems="center">
        {skillSetsCount && (
          <Pagination
            showXsNav
            total={skillSetsCount}
            page={currentPage}
            pageSize={limit}
            onPageChange={(_, page) => {
              onPageChange(page);
            }}
            onPageSizeChange={(_, size) => {
              onLimitChange(size);
              onPageChange(1);
            }}
          />
        )}
      </Grid>
    </Grid>
  );
};
